import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { ImportResult, Category, CSVImportCategoryMode, CSVImportOptions, CSVImportSplitSize } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { TooltipHelp } from './TooltipHelp';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SPLIT_SIZE_OPTIONS: CSVImportSplitSize[] = [250, 500, 1000];
const DEFAULT_SPLIT_SIZE: CSVImportSplitSize = 500;

export function CSVImportModal({ isOpen, onClose, onSuccess }: CSVImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [isCountingRows, setIsCountingRows] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [originalFileName, setOriginalFileName] = useState<string>('');
  
  // Estados para seleção de categoria
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [applyDefaultCategory, setApplyDefaultCategory] = useState(false);
  const [categoryMode, setCategoryMode] = useState<CSVImportCategoryMode>('existing');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitSize, setSplitSize] = useState<CSVImportSplitSize>(DEFAULT_SPLIT_SIZE);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Contextos para informações do usuário e tenant
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const { theme } = useTheme();

  // Buscar categorias quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await apiService.getCategories({ pageSize: 100 });
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      // Não mostrar erro para o usuário, apenas deixar sem categorias
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  if (!isOpen) return null;

  // Helpers para detecção de Excel
  const isExcelFile = (f: File): boolean => {
    const excelTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];
    return excelTypes.includes(f.type) || /\.(xlsx|xls)$/i.test(f.name);
  };

  const isAcceptedFile = (f: File): boolean => {
    return f.type === 'text/csv' || f.name.endsWith('.csv') || isExcelFile(f);
  };

  // Converte Excel → CSV usando SheetJS (primeira aba)
  const convertExcelToCSV = (f: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            reject(new Error('O arquivo Excel está vazio (nenhuma aba encontrada).'));
            return;
          }
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          if (!firstSheet) {
            reject(new Error('A primeira aba do Excel está vazia.'));
            return;
          }
          const csvContent = XLSX.utils.sheet_to_csv(firstSheet);
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const csvFile = new File([blob], f.name.replace(/\.(xlsx|xls)$/i, '.csv'), { type: 'text/csv' });
          resolve(csvFile);
        } catch (error) {
          reject(new Error('Erro ao converter arquivo Excel. Verifique se o arquivo não está corrompido.'));
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo Excel'));
      reader.readAsArrayBuffer(f);
    });
  };

  // Função para contar linhas do CSV
  const countCSVRows = (csvFile: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n');
          const dataLines = lines.filter(line => line.trim().length > 0);
          const rowCount = dataLines.length > 0 ? dataLines.length - 1 : 0;
          resolve(rowCount);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsText(csvFile);
    });
  };

  // Função para processar arquivo (usada tanto para input quanto drag and drop)
  const processFile = async (selectedFile: File, inputElement?: HTMLInputElement) => {
    if (!isAcceptedFile(selectedFile)) {
      toast.error('Por favor, selecione um arquivo CSV ou Excel (.xlsx, .xls)');
      return;
    }

    // Limite de tamanho: 50MB para prevenir travamento do navegador
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error('O arquivo é muito grande (máx. 50MB). Divida em arquivos menores.');
      return;
    }

    setIsCountingRows(true);
    setOriginalFileName(selectedFile.name);

    try {
      // Se for Excel, converter para CSV primeiro
      let csvFile = selectedFile;
      if (isExcelFile(selectedFile)) {
        toast.loading('Convertendo Excel para CSV...', { id: 'excel-convert' });
        csvFile = await convertExcelToCSV(selectedFile);
        toast.success(`Excel convertido! (${selectedFile.name})`, { id: 'excel-convert' });
      }

      // Contar linhas do CSV (já convertido se era Excel)
      const count = await countCSVRows(csvFile);
      setRowCount(count);
      
      // Verificar limite de 5000 contatos
      if (count > 5000) {
        toast.error(`A planilha tem ${count} contatos. O limite máximo é de 5.000 contatos por importação. Por favor, divida a planilha em arquivos menores.`);
        setFile(null);
        setRowCount(null);
        // Limpar o input se fornecido
        if (inputElement) {
          inputElement.value = '';
        }
        return;
      }

      setFile(csvFile);
      setImportResult(null);
      
      if (count > 0) {
        toast.success(`Arquivo selecionado: ${count} contatos encontrados`);
      }
    } catch (error: any) {
      const msg = error?.message || 'Erro ao processar arquivo';
      toast.error(msg);
      toast.dismiss('excel-convert');
      if (inputElement) {
        inputElement.value = '';
      }
    } finally {
      setIsCountingRows(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      await processFile(selectedFile, event.target);
    }
  };

  // Funções para drag and drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Só remove o estado se realmente saiu da área de drop
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const acceptedFile = files.find((f: File) => isAcceptedFile(f));
    
    if (acceptedFile) {
      await processFile(acceptedFile);
    } else {
      toast.error('Por favor, selecione um arquivo CSV ou Excel (.xlsx, .xls)');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo CSV ou Excel');
      return;
    }

    setIsUploading(true);
    try {
      let categoryOptions: CSVImportOptions | undefined;

      if (applyDefaultCategory) {
        if (categoryMode === 'existing') {
          if (!selectedCategoryId) {
            toast.error('Selecione uma categoria existente');
            setIsUploading(false);
            return;
          }

          categoryOptions = {
            categoryMode: 'existing',
            defaultCategoryId: selectedCategoryId,
          };
        } else {
          const trimmedCategoryName = newCategoryName.trim();
          if (!trimmedCategoryName) {
            toast.error('Informe o nome da nova categoria');
            setIsUploading(false);
            return;
          }

          categoryOptions = {
            categoryMode: 'new',
            newCategoryName: trimmedCategoryName,
            splitEnabled,
            splitSize,
          };
        }
      }
      
      const result = await apiService.importCSV(file, categoryOptions);
      // Garantir que o resultado sempre tenha um array de erros e campos de duplicatas
      const safeResult = {
        ...result,
        errors: result.errors || [],
        duplicatesSkipped: result.duplicatesSkipped || 0,
        csvDuplicatesRemoved: result.csvDuplicatesRemoved || 0
      };
      setImportResult(safeResult);

      if (result.success) {
        toast.success(`Importação concluída! ${result.successfulImports} contatos importados.`);
        onSuccess();
      } else {
        toast(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`, {
          icon: '⚠️',
          style: {
            border: '1px solid #f59e0b',
            color: '#92400e',
          },
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao importar arquivo';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-contatos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      toast.error('Erro ao baixar template CSV');
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setRowCount(null);
    setIsCountingRows(false);
    setIsDragOver(false);
    setSelectedCategoryId('');
    setApplyDefaultCategory(false);
    setCategoryMode('existing');
    setNewCategoryName('');
    setSplitEnabled(false);
    setSplitSize(DEFAULT_SPLIT_SIZE);
    setOriginalFileName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm">
      <div className="rounded-2xl shadow-2xl w-full max-w-2xl border transition-colors flex flex-col max-h-[90vh]" style={{
        backgroundColor: theme === 'dark' ? '#1D1E24' : 'var(--bg-card)',
        borderColor: 'var(--border-color)'
      }} role="dialog" aria-labelledby="import-title">
        {/* Header fixo */}
        {/* MODIFICADO: Header com cores do tema */}
        {/* ANTES: cores fixas */}
        <div className="text-center p-8 pb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          </div>
          <h2 id="import-title" className="text-xl font-semibold text-n-slate-12 flex items-center justify-center gap-2">
            <span>Importar Contatos</span>
            <TooltipHelp id="contacts_import" />
          </h2>
          <p className="mt-2 text-n-slate-11">
            Importe múltiplos contatos usando um arquivo CSV ou Excel (.xlsx, .xls)
          </p>
        </div>

        {/* Conteúdo com scroll */}
        <div className="flex-1 overflow-y-auto px-8 py-4">
          <div className="space-y-6">
          
          {/* SuperAdmin Tenant Info */}
          {user?.role === 'SUPERADMIN' && (
            /* MODIFICADO: Box SuperAdmin adaptado */
            /* ANTES: bg-gradient ... amber fixo */
            <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>👑 SuperAdmin - Contexto de Operação</h3>
                  <div className="space-y-2">
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <strong>Tenant Atual:</strong> {selectedTenant ? `${selectedTenant.name} (${selectedTenant.slug})` : 'Nenhum tenant selecionado'}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      ℹ️ Como SuperAdmin, você pode importar contatos para qualquer tenant. 
                      As categorias mostradas são do tenant atual. 
                      Se não houver tenant selecionado, você verá todas as categorias do sistema.
                    </p>
                    {!selectedTenant && (
                      <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          ⚠️ <strong>Atenção:</strong> Sem tenant selecionado, as categorias podem ser de diferentes tenants. 
                          Certifique-se de que a categoria escolhida pertence ao tenant de destino.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Download */}
          {/* MODIFICADO: Card Template CSV com tema */}
          {/* ANTES: bg-gradient azul claro fixo */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Template CSV</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Baixe o template para ver o formato correto do arquivo CSV e facilitar sua importação.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors duration-200 flex items-center gap-2"
                  style={{ backgroundColor: 'var(--astra-dark-blue)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Baixar Template CSV
                </button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            {/* MODIFICADO: Label do upload */}
            {/* ANTES: text-gray-700 */}
            <label htmlFor="csv-file" className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              Selecionar arquivo CSV ou Excel
            </label>
            <div 
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200`}
              style={{
                borderColor: isDragOver ? '#3b82f6' : 'var(--border-color)',
                backgroundColor: isDragOver ? (theme === 'dark' ? 'rgba(59,130,246,0.08)' : '#eff6ff') : 'transparent',
                transform: isDragOver ? 'scale(1.02)' : 'none'
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                id="csv-file"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
                className="hidden"
              />
              <label htmlFor="csv-file" className="cursor-pointer">
                <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-colors duration-200" style={{ backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f3f4f6' }}>
                  <svg className="w-6 h-6" style={{ color: isDragOver ? '#3b82f6' : (theme === 'dark' ? '#a1a1aa' : '#9ca3af') }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="font-medium transition-colors duration-200" style={{ color: isDragOver ? '#3b82f6' : 'var(--text-secondary)' }}>
                  {isDragOver ? 'Solte o arquivo aqui' : 'Clique para selecionar arquivo'}
                </p>
                <p className="text-sm mt-1 transition-colors duration-200" style={{ color: isDragOver ? '#3b82f6' : 'var(--text-secondary)' }}>
                  ou arraste e solte aqui
                </p>
              </label>
            </div>
            {/* Loading state para contar linhas */}
            {isCountingRows && (
              <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: theme === 'dark' ? 'rgba(59,130,246,0.08)' : '#eff6ff', border: `1px solid ${theme === 'dark' ? 'rgba(59,130,246,0.25)' : '#bfdbfe'}` }}>
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-blue-700 font-medium">
                    Analisando arquivo...
                  </p>
                </div>
              </div>
            )}

            {/* Arquivo selecionado com contador */}
            {file && rowCount !== null && (
              <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: theme === 'dark' ? 'rgba(16,185,129,0.1)' : '#ecfdf5', border: `1px solid ${theme === 'dark' ? 'rgba(16,185,129,0.25)' : '#a7f3d0'}` }}>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-green-700 font-medium">
                      Arquivo selecionado: {originalFileName || file.name}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {rowCount} contatos encontrados • Limite: 5.000 contatos
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Category Selection */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Vincular Categoria</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  A categoria será aplicada apenas aos contatos sem categoria no CSV
                </p>
                {user?.role === 'SUPERADMIN' && (
                  <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <strong>SuperAdmin:</strong> {selectedTenant
                        ? `Categorias do tenant "${selectedTenant.name}"`
                        : 'Categorias de todos os tenants (use com cuidado)'
                      }
                    </p>
                  </div>
                )}

                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyDefaultCategory}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setApplyDefaultCategory(checked);
                      if (checked && categories.length === 0) {
                        setCategoryMode('new');
                      }
                    }}
                    className="w-4 h-4 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Vincular categoria aos contatos sem categoria
                  </span>
                </label>

                {applyDefaultCategory && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCategoryMode('existing')}
                        disabled={categories.length === 0}
                        className="px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: categoryMode === 'existing' ? 'var(--astra-dark-blue)' : 'var(--bg-card)',
                          borderColor: categoryMode === 'existing' ? 'var(--astra-dark-blue)' : 'var(--border-color)',
                          color: categoryMode === 'existing' ? '#ffffff' : 'var(--text-primary)'
                        }}
                      >
                        Categoria existente
                      </button>
                      <button
                        type="button"
                        onClick={() => setCategoryMode('new')}
                        className="px-4 py-3 rounded-lg border text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: categoryMode === 'new' ? 'var(--astra-dark-blue)' : 'var(--bg-card)',
                          borderColor: categoryMode === 'new' ? 'var(--astra-dark-blue)' : 'var(--border-color)',
                          color: categoryMode === 'new' ? '#ffffff' : 'var(--text-primary)'
                        }}
                      >
                        Criar nova
                      </button>
                    </div>

                    {categoryMode === 'existing' ? (
                      <div>
                        <select
                          value={selectedCategoryId}
                          onChange={(e) => setSelectedCategoryId(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                          disabled={isLoadingCategories || categories.length === 0}
                        >
                          <option value="">Selecione uma categoria</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.nome}
                            </option>
                          ))}
                        </select>
                        {categories.length === 0 && (
                          <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                            Nenhuma categoria existente encontrada. Use a opção criar nova.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="new-category-name" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                            Nome da categoria
                          </label>
                          <input
                            id="new-category-name"
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Ex: Clientes VIP"
                            className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                          />
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={splitEnabled}
                            onChange={(e) => setSplitEnabled(e.target.checked)}
                            className="w-4 h-4 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Dividir categoria em blocos de contatos
                          </span>
                        </label>

                        {splitEnabled && (
                          <div>
                            <label htmlFor="split-size" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                              Tamanho dos blocos
                            </label>
                            <select
                              id="split-size"
                              value={splitSize}
                              onChange={(e) => setSplitSize(Number(e.target.value) as CSVImportSplitSize)}
                              className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                            >
                              {SPLIT_SIZE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option} contatos
                                </option>
                              ))}
                            </select>
                            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                              Exemplo: Clientes VIP 1 - {splitSize}, Clientes VIP {splitSize + 1} - {splitSize * 2}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Categorias informadas nas colunas categoriaId ou categoria do CSV serão preservadas.
                      {user?.role === 'SUPERADMIN' && !selectedTenant && (
                        <span className="block mt-1 font-medium" style={{ color: 'var(--text-secondary)' }}>
                          SuperAdmin: certifique-se de que o tenant de destino está correto.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Import Instructions */}
          {/* MODIFICADO: Instruções com tema */}
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Instruções de Importação</h3>
                <ul className="text-sm space-y-2" style={{ color: 'var(--text-secondary)' }}>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    O arquivo deve estar em formato CSV ou Excel (.xlsx, .xls)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    <span>⚠️ <strong>Limite máximo:</strong> 5.000 contatos por importação</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Colunas obrigatórias: <strong>nome/name</strong> e <strong>telefone/phone_number</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Colunas opcionais: <strong>email</strong>, <strong>observacoes</strong>, <strong>tags</strong>, <strong>categoriaId</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Para vincular à categoria, use o <strong>ID (UUID)</strong> da categoria no campo categoriaId
                  </li>
                  {user?.role === 'SUPERADMIN' && (
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                      <span>👑 <strong>SuperAdmin:</strong> Categorias são validadas automaticamente - apenas categorias do tenant de destino serão aceitas</span>
                    </li>
                  )}
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    <span>✨ <strong>Reconhecimento automático</strong> de múltiplos formatos de planilhas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Para tags, separe múltiplas tags com vírgula na mesma célula
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Use o template como referência para melhor resultado
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>🧹 <strong>Higienização automática:</strong> Duplicatas são detectadas por telefone + categoria</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    <span>ℹ️ <strong>Mesmo telefone:</strong> Pode existir em categorias diferentes, mas não na mesma categoria</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className={`border rounded-xl p-6`} style={{ backgroundColor: importResult.success ? (theme === 'dark' ? 'rgba(16,185,129,0.1)' : '#ecfdf5') : (theme === 'dark' ? 'rgba(250,204,21,0.08)' : '#fffbeb'), borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${importResult.success ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  <svg className={`w-6 h-6 ${importResult.success ? 'text-green-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {importResult.success ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    )}
                  </svg>
                </div>
                <h3 className={`font-semibold text-lg ${importResult.success ? 'text-green-900' : 'text-yellow-900'}`}>
                  Resultado da Importação
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                  <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{importResult.totalRows}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total de linhas</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                  <div className="text-2xl font-bold text-green-600">{importResult.successfulImports}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Sucessos</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                  <div className="text-2xl font-bold text-red-600">{importResult.failedImports}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Erros</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                  <div className="text-2xl font-bold text-orange-600">{importResult.duplicatesSkipped + importResult.csvDuplicatesRemoved}</div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Duplicatas</div>
                </div>
              </div>

              {/* Detalhes das duplicatas */}
              {(importResult.duplicatesSkipped > 0 || importResult.csvDuplicatesRemoved > 0) && (
                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: theme === 'dark' ? 'rgba(234,179,8,0.08)' : '#fffbeb', border: `1px solid ${theme === 'dark' ? 'rgba(234,179,8,0.25)' : '#fde68a'}` }}>
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>📊 Detalhes das Duplicatas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {importResult.csvDuplicatesRemoved > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                        <span className="text-orange-800">
                          <strong>{importResult.csvDuplicatesRemoved}</strong> duplicatas removidas do CSV (telefone + categoria)
                        </span>
                      </div>
                    )}
                    {importResult.duplicatesSkipped > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-orange-800">
                          <strong>{importResult.duplicatesSkipped}</strong> telefones já existem na mesma categoria
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-orange-700 mt-2">
                    ℹ️ Duplicatas são detectadas por telefone + categoria. O mesmo telefone pode existir em categorias diferentes, mas não na mesma categoria
                  </p>
                </div>
              )}

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-3">
                  <details className="cursor-pointer">
                    <summary className="text-sm font-semibold text-red-700 hover:text-red-800">Ver erros ({importResult.errors.length})</summary>
                    <div className="mt-3 rounded-lg p-3 max-h-40 overflow-y-auto" style={{ backgroundColor: theme === 'dark' ? 'rgba(239,68,68,0.08)' : '#fef2f2', border: `1px solid ${theme === 'dark' ? 'rgba(239,68,68,0.25)' : '#fecaca'}` }}>
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="text-sm text-red-600 py-1 border-b border-red-100 last:border-0">{error}</div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Footer fixo com botões */}
        <div className="border-t px-8 py-6" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex gap-4">
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
            >
              Fechar
            </button>
            <button
              onClick={handleImport}
              disabled={!file || isUploading}
              className="btn-primary flex-1 py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Importando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Importar Contatos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
